import { TreeItem2, useTreeItem2Utils } from '@mui/x-tree-view';
import { Dispatch, FC, ReactNode, MouseEvent, SetStateAction, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ALL_MODULES_TREE_ITEM_ID } from '../constants/modulesConstants';
import { selectModules } from '../store/selectors';
import CustomTreeItemLabel, { CustomTreeItemLabelProps } from './CustomTreeItemLabel';
import { isMac } from '@shared/constants/otherConstants';
import { TreeViewItemId } from '@mui/x-tree-view/models';
import { ModuleID } from '../types';
import { TreeItem2Props } from '@mui/x-tree-view/TreeItem2/TreeItem2.types';
import { RichTreeViewApiRef } from '@mui/x-tree-view/RichTreeView/RichTreeView.types';
import { findModuleChildrenById, getModuleParentPathByModuleId } from '@pages/modules/utils/modulesUtils';

export interface CustomTreeItemProps extends TreeItem2Props {
    itemId: TreeViewItemId;
    apiRef: RichTreeViewApiRef;
    setExpandedModules: Dispatch<SetStateAction<ModuleID[]>>;
    setSelectedModules: Dispatch<SetStateAction<ModuleID[]>>;
    children: ReactNode[];
    searchString: string;
}

const CustomTreeItem: FC<CustomTreeItemProps> = ({
    itemId,
    apiRef,
    setExpandedModules,
    setSelectedModules,
    children,
    searchString,
    ...restProps
}) => {
    const modules = useSelector(selectModules);

    const { interactions } = useTreeItem2Utils({
        itemId: itemId,
        children: children,
    });

    const selectedModuleWithChildrenIds = useMemo(() => {
        if (itemId === ALL_MODULES_TREE_ITEM_ID) {
            return [];
        }

        return [String(itemId), ...findModuleChildrenById(itemId, modules).map((object) => String(object.pk))];
    }, [itemId, modules]);

    const parentAndChildPathOfSelectedModule = useMemo(() => {
        if (itemId === ALL_MODULES_TREE_ITEM_ID) {
            return [];
        }

        return Array.from(
            new Set([
                ...selectedModuleWithChildrenIds,
                ...getModuleParentPathByModuleId(itemId, modules, 'pk').map((id: number) => String(id)),
            ]),
        );
    }, [modules, itemId, selectedModuleWithChildrenIds]);

    const handleExpandNested = () => {
        setExpandedModules((currentExpandedItems) =>
            Array.from(new Set([...currentExpandedItems, ...selectedModuleWithChildrenIds])),
        );
    };

    const handleCollapseOthers = () => {
        setExpandedModules((currentExpandedItems) => [
            ALL_MODULES_TREE_ITEM_ID,
            ...currentExpandedItems.filter((item) => parentAndChildPathOfSelectedModule.includes(item)),
        ]);
    };

    const handleIconContainerClick = (event: MouseEvent) => {
        event.stopPropagation();
        const withCtrlKey = isMac ? event.metaKey : event.ctrlKey;
        const withShiftKey = event.shiftKey;

        if (withShiftKey) {
            handleExpandNested();
        } else if (withCtrlKey) {
            handleCollapseOthers();
        } else {
            interactions.handleExpansion(event);
        }
    };

    const handleContentClick = () => {
        if (itemId === ALL_MODULES_TREE_ITEM_ID) {
            return;
        }

        const itemIdNumber = Number(itemId);
        setSelectedModules((selectedModules) =>
            selectedModules.includes(itemIdNumber)
                ? selectedModules.filter((m) => m !== itemIdNumber)
                : [...selectedModules, itemIdNumber],
        );
    };

    return (
        <TreeItem2
            {...restProps}
            itemId={itemId}
            children={children}
            slots={{
                label: CustomTreeItemLabel,
            }}
            slotProps={{
                content: {
                    onClick: handleContentClick,
                },
                iconContainer: {
                    onClick: handleIconContainerClick,
                    style: {
                        display: itemId === ALL_MODULES_TREE_ITEM_ID ? 'none' : '',
                        width: '16px',
                    },
                },
                label: {
                    itemHasChildren: selectedModuleWithChildrenIds.length > 1,
                    itemId: itemId,
                    searchString,
                } as CustomTreeItemLabelProps,
                checkbox: {
                    style: {
                        display: itemId === ALL_MODULES_TREE_ITEM_ID ? 'none' : '',
                    },
                },
            }}
        />
    );
};

export default CustomTreeItem;
